import { useMemo } from "react";
import {
  useBudgetMeta,
  useBudgetProviders,
  useBudgetServicesState,
  useBudgetSuppliersState,
} from "../../../../contexts/BudgetContext/projectbudgets.context";
import { CalcOption } from "../../../../types/budget/budget";
import { parseSupplierId } from "../../../../utils/budget-supplier-id";
import { isSupplierVisibleInVersion } from "../../../../utils/budget-stage-visibility";

export type TransferTargetOption = {
  /** Id interno na `supplierList` (pode vir sufixado). */
  supplierId: string;
  /** `_id` real do fornecedor. */
  realId: string;
  label: string;
};

/**
 * Fornecedores que podem receber uma categoria ou um serviço sem sair da forma
 * de cobrança atual. Exclui o próprio fornecedor de origem, linhas ainda sem
 * fornecedor escolhido e fornecedores que não existem nesta etapa (a
 * `supplierList` não é versionada — ver `utils/budget-stage-visibility.ts`).
 *
 * Compartilhado entre a transferência de categoria (`BudgetSupplier`) e a de
 * serviço (`BudgetItem`), que precisam exatamente da mesma lista.
 */
export function useTransferTargets(
  calcOption: CalcOption | string | undefined,
  currentSupplierId: string
): TransferTargetOption[] {
  const { supplierList } = useBudgetSuppliersState();
  const { budgetServices } = useBudgetServicesState();
  const { providersById } = useBudgetProviders();
  const { versionInfo } = useBudgetMeta();

  const version = versionInfo?.version ?? 0;

  return useMemo(() => {
    if (!calcOption) return [];

    return (supplierList ?? [])
      .filter(
        (el) =>
          el.calcOption === calcOption &&
          !!el.supplierId &&
          el.supplierId !== currentSupplierId &&
          isSupplierVisibleInVersion(
            budgetServices,
            el.supplierId,
            calcOption,
            version,
            el.deletedInVersions,
            versionInfo?.status
          )
      )
      .map((el) => ({
        supplierId: el.supplierId,
        realId: parseSupplierId(el.supplierId),
        label:
          providersById[parseSupplierId(el.supplierId)]?.name ||
          "Fornecedor sem nome",
      }));
  }, [
    supplierList,
    budgetServices,
    calcOption,
    currentSupplierId,
    version,
    providersById,
  ]);
}
